import React, { ReactElement, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { usePdfThumbnailRender } from 'hooks/useFileType/usePdfThumbnailRender';
import { isImagePreviewUrl } from 'shared/utils/sourceUrl';
import {
  ThumbnailErrorFallback,
  ThumbnailLoadingOverlay,
} from './common';
import {
  getThumbnailImageStyle,
  THUMBNAIL_CONTAINER_SX,
} from './styles';
import { ThumbnailProps } from './types';

interface PdfThumbnailProps extends ThumbnailProps {
  /** When true, defers client pdf.js render until parent sets lazy=false (e.g. on visibility). */
  lazy?: boolean;
  thumbnailCacheKey?: string;
}

interface ThumbnailImageContentProps {
  src: string;
  thumbnailSize: number;
  onClick?: (e: React.SyntheticEvent) => void;
  showOverlay: boolean;
}

function ThumbnailImageContent({
  src,
  thumbnailSize,
  onClick,
  showOverlay,
}: ThumbnailImageContentProps): ReactElement {
  const [imageLoading, setImageLoading] = useState(true);
  const imageStyle = useMemo(
    () => ({
      ...getThumbnailImageStyle(thumbnailSize),
      opacity: imageLoading ? 0 : 1,
      transition: 'opacity 0.2s ease',
    }),
    [imageLoading, thumbnailSize]
  );

  return (
    <Box onClick={onClick} sx={THUMBNAIL_CONTAINER_SX}>
      {(showOverlay || imageLoading) && <ThumbnailLoadingOverlay />}
      <img
        src={src}
        alt=''
        style={imageStyle}
        crossOrigin='use-credentials'
        loading='eager'
        decoding='async'
        onLoad={() => setImageLoading(false)}
        onError={() => setImageLoading(false)}
      />
    </Box>
  );
}

export const PdfThumbnail: React.FC<PdfThumbnailProps> = ({
  src,
  thumbnailSize,
  onClick,
  fileType,
  fileExtension,
  lazy = false,
  thumbnailCacheKey,
}): ReactElement => {
  const shouldRenderClient = !lazy;
  const isServerImage = isImagePreviewUrl(src);

  const { imageSrc, isLoading, error } = usePdfThumbnailRender(src, thumbnailSize, {
    enabled: shouldRenderClient && !isServerImage,
    cacheKey: thumbnailCacheKey,
  });

  if (error) {
    return (
      <ThumbnailErrorFallback
        onClick={onClick}
        fileType={fileType}
        fileExtension={fileExtension}
      />
    );
  }

  if (isServerImage && src) {
    return (
      <ThumbnailImageContent
        // A new image loads from the start: its loading state resets.
        key={src}
        src={src}
        thumbnailSize={thumbnailSize}
        onClick={onClick}
        showOverlay={false}
      />
    );
  }

  if (imageSrc) {
    return (
      <ThumbnailImageContent
        key={imageSrc}
        src={imageSrc}
        thumbnailSize={thumbnailSize}
        onClick={onClick}
        showOverlay={isLoading}
      />
    );
  }

  return (
    <Box onClick={onClick} sx={THUMBNAIL_CONTAINER_SX}>
      <ThumbnailLoadingOverlay />
    </Box>
  );
};
